import React from 'react';

import { MDText } from 'i18n-react';

import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import { POST_SALE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsPostSale';

import { StepList } from 'Storybook/components/local/StepList/StepList';

import { Frame } from '../../../postSale/components/Frame/Frame';
import { useSaleDetailsSub } from '../../hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../hooks/useVehicleCollectionSub';

import LocalTexts from './CollectionChecklist.texts.json';

import styles from './CollectionChecklist.scss';

const LocalT = new MDText(LocalTexts);

export const dayOfCollectionLink = 'https://help.motorway.co.uk/hc/en-gb/articles/4410620430866-What-happens-on-the-day-of-collection';
export const prepareHandoverLink = 'https://help.motorway.co.uk/hc/en-gb/articles/4410631249810-What-do-I-need-to-have-ready-on-the-day-of-sale';
export const negativeEquityLink = 'https://help.motorway.co.uk/hc/en-gb/articles/7370527234972-Does-Motorway-pay-off-finance';

export type CollectionChecklistProps = Partial<{
  ctaLabel?: React.ReactNode;
  description?: React.ReactNode;
  heading: React.ReactNode;
  image?: React.ReactNode;
  nested?: boolean;
}>

type GetStepsProps = Partial<{
  isNESettled: boolean;
  negativeEquity: boolean;
  profilePath: string;
}>

const getSteps = ({ isNESettled = false, negativeEquity = false, profilePath }: GetStepsProps) => {
  const steps = [
    ...(negativeEquity && !isNESettled ? [{
      content: (
        <p data-thc-style-override data-item="negativeEquity">
          {LocalT.translate('steps.negativeEquity')}
          <span>
            <Hyperlink
              href={negativeEquityLink}
              label={LocalT.translate('steps.negativeEquityLink')}
              onClick={() => POST_SALE_SNOW_PLOW_EVENTS.CHECKLIST_NEGATIVE_EQUITY_LINK_CLICKED(
                { salePage: true, url: negativeEquityLink },
              )}
              target="_blank"
            />
          </span>
        </p>
      ),
      id: 1,
    }] : []),
    {
      content: (
        <p data-thc-style-override data-item="dayOfCollection">
          {LocalT.translate(
            'steps.dayOfCollection',
            {
              dayOfCollection: <Hyperlink
                href={dayOfCollectionLink}
                label={LocalT.translate('steps.dayOfCollectionLink')}
                onClick={() => POST_SALE_SNOW_PLOW_EVENTS.CHECKLIST_COLLECTION_DAY_LINK_CLICKED(
                  { salePage: true, url: dayOfCollectionLink },
                )}
                target='_blank'
              />,
            },
          )}
        </p>
      ),
      id: 2,
    },
    {
      content: (
        <p data-thc-style-override data-item="prepareHandover">
          {LocalT.translate(
            'steps.prepareHandover',
            {
              preparation: <Hyperlink
                href={prepareHandoverLink}
                label={LocalT.translate('steps.prepareHandoverLink')}
                onClick={() => POST_SALE_SNOW_PLOW_EVENTS.CHECKLIST_PREPARE_HANDOVER_LINK_CLICKED(
                  { salePage: true, url: prepareHandoverLink },
                )}
                target="_blank"
              />,
            },
          )}
        </p>
      ),
      id: 3,
    },
    {
      content: (
        <p data-thc-style-override data-item="bookmark">
          {LocalT.translate(
            'steps.bookmark',
            {
              vehicleProfile: <Hyperlink
                href={profilePath}
                label={LocalT.translate('steps.bookmarkLink')}
                onClick={() => POST_SALE_SNOW_PLOW_EVENTS.CHECKLIST_VEHICLE_DETAILS_LINK_CLICKED(
                  { salePage: true, url: profilePath! },
                )}
                target="_blank"
              />,
            },
          )}
        </p>
      ),
      id: 4,
    },
  ];

  return steps;
};

export const CollectionChecklist = () => {
  const { hasNegativeEquity, vehicleProfilePath } = useSaleDetailsSub();
  const { isNESettled } = useVehicleCollectionSub();

  const steps = getSteps({
    isNESettled,
    negativeEquity: hasNegativeEquity,
    profilePath: vehicleProfilePath,
  });

  return (
    <Frame className={styles.collectionChecklist}>
      <h5 className={styles.heading}>{LocalT.translate('heading')}</h5>
      <p data-thc-style-override className={styles.subheading}>{LocalT.translate('subheading')}</p>
      <StepList align="center" steps={steps} />
    </Frame>
  );
};
